import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Users, Book, Award, Zap } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import TrainerGrades from './TrainerGrades';
import TrainerStudents from './TrainerStudents';

const TrainerDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalSubjects: 0,
    totalGrades: 0,
    averageGrade: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Get grades created by this trainer
      const { data: gradesData, error: gradesError } = await supabase
        .from('grades')
        .select('*')
        .eq('trainer_id', user.id);

      if (gradesError) {
        throw gradesError;
      }

      // Count unique students
      const uniqueStudents = new Set();
      const uniqueSubjects = new Set();
      let totalGradeValue = 0;

      gradesData?.forEach(grade => {
        uniqueStudents.add(grade.student_id);
        uniqueSubjects.add(grade.subject_id);
        totalGradeValue += grade.grade;
      });

      const avgGrade = gradesData?.length > 0 
        ? Math.round((totalGradeValue / gradesData.length) * 10) / 10 
        : 0;

      setStats({
        totalStudents: uniqueStudents.size,
        totalSubjects: uniqueSubjects.size,
        totalGrades: gradesData?.length || 0,
        averageGrade: avgGrade,
      });
    } catch (error) {
      console.error('Error fetching trainer stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const DashboardHome = () => (
    <>
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Tableau de Bord Formateur</h1>
        <p className="mt-2 text-gray-600">
          Gérez les notes de vos étudiants
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">Étudiants</p>
              <p className="text-3xl font-bold mt-2 text-primary-600">
                {isLoading ? '...' : stats.totalStudents}
              </p>
            </div>
            <div className="bg-primary-100 p-3 rounded-full">
              <Users className="h-6 w-6 text-primary-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">Matières</p>
              <p className="text-3xl font-bold mt-2 text-secondary-600">
                {isLoading ? '...' : stats.totalSubjects}
              </p>
            </div>
            <div className="bg-secondary-100 p-3 rounded-full">
              <Book className="h-6 w-6 text-secondary-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">Notes</p>
              <p className="text-3xl font-bold mt-2 text-accent-600">
                {isLoading ? '...' : stats.totalGrades}
              </p>
            </div>
            <div className="bg-accent-100 p-3 rounded-full">
              <Award className="h-6 w-6 text-accent-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">Moyenne</p>
              <p className="text-3xl font-bold mt-2 text-success-500">
                {isLoading ? '...' : stats.averageGrade}
              </p>
            </div>
            <div className="bg-success-50 p-3 rounded-full">
              <Zap className="h-6 w-6 text-success-500" />
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gérer les notes</h3>
          <p className="text-gray-600 mb-4">
            Créez, consultez et modifiez les notes de vos étudiants.
          </p>
          <a 
            href="/trainer/grades" 
            className="btn btn-primary inline-block"
          >
            Gérer les notes
          </a>
        </div>
        
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Mes étudiants</h3>
          <p className="text-gray-600 mb-4">
            Consultez la liste de vos étudiants et leurs performances.
          </p>
          <a 
            href="/trainer/students" 
            className="btn btn-outline inline-block"
          >
            Voir mes étudiants
          </a>
        </div>
      </div>
    </>
  );

  return (
    <div className="animate-fade-in">
      <Routes>
        <Route index element={<DashboardHome />} />
        <Route path="grades" element={<TrainerGrades />} />
        <Route path="students" element={<TrainerStudents />} />
      </Routes>
    </div>
  );
};

export default TrainerDashboard;